import React, { useState, useRef, useEffect } from "react";
import { Form, Input, Button, Card, message, Modal } from "antd";
import { UserOutlined, LockOutlined } from "@ant-design/icons";
import "./Login.css"; // Import CSS
import logo from "../../assets/logo.png";
import axios from "axios";
import consts from "../../constant";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchAdmin } from "../../store/slices/userSlice";

const LOGIN_MESSAGE_KEY = "login-feedback";
const BUTTON_LOADER_DELAY = 800;

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "transparent",
  boxShadow: 0,
  borderRadius: "0px",
  outline: "none",
  "@media(max-width:767.98px)": {
    width: "75%",
  },
  p: 0,
};

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const Login = () => {

  const dispatch = useDispatch();
  const { permissions, role } = useSelector((state) => state.user);
  console.log("🚀 ~ Login ~ permissions, role:", permissions, role)
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const fullOtp = otp.join("");
  const navigate = useNavigate();
  const [otpModel, setOtpModel] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const submitLockedRef = useRef(false);
  const inputsRef = useRef([]);
  const [adminemail, setAdminEmail] = useState("");

  const showLoginMessage = (type, content) => {
    messageApi.open({
      key: LOGIN_MESSAGE_KEY,
      type,
      content: content || "Something went wrong",
    });
  };

  const finishLoginAttempt = async (type, content) => {
    await delay(BUTTON_LOADER_DELAY);
    setLoading(false);
    submitLockedRef.current = false;
    showLoginMessage(type, content);
  };

  const handleOtp = (e, index) => {
    const value = e.target.value;

    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < inputsRef.current.length - 1) {
      inputsRef.current[index + 1].focus();
    }
  };
  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !e.target.value && index > 0) {
      inputsRef.current[index - 1].focus();
    }
  };

  const clearOtp = () => {
    const emptyOtp = Array(otp.length).fill("");
    setOtp(emptyOtp);

    // Optional: focus first input again
    setTimeout(() => {
      inputsRef.current[0]?.focus();
    }, 0);
  };

  const handlePaste = (e) => {
    e.preventDefault();

    const pastedData = e.clipboardData.getData("text").replace(/\D/g, ""); // only digits
    const digits = pastedData.slice(0, otp.length).split("");

    const newOtp = [...otp];
    digits.forEach((digit, i) => {
      newOtp[i] = digit;
      inputsRef.current[i].value = digit; // optional: force set for quick visual sync
    });

    setOtp(newOtp);

    // Focus the next empty input (or last one)
    const nextIndex =
      digits.length < otp.length ? digits.length : otp.length - 1;
    inputsRef.current[nextIndex]?.focus();
  };

  const responseTimer = (userRole, userPermissions) => {
    const normalizedPermissions = userPermissions?.map((p) => p.toLowerCase().trim(),) || [];

    if (userRole === "admin" || userRole === "superadmin" || normalizedPermissions.includes("dashboard")) {
      navigate(`/${consts.route + "/dashboard"}`);
    } else {
      // Find first available permission route
      const permissionRoutes = {
        users: "/usersHistory",
        "users wallet history": "/users",
        "crypto history": "/crypto/highlow",
        "trade settings": "/tradesetting",
        deposit: "/deposit",
        withdraw: "/withdraw",
        pairs: "/pairs",
        supports: "/supports",
        settings: "/settings",
        "sub admin": "/subadmin/rolemanage",
      };

      // Find first allowed route by checking normalized keys
      const firstAllowedPermission = userPermissions?.find((p) => permissionRoutes[p.toLowerCase().trim()],);

      if (firstAllowedPermission) {
        navigate(`/${consts.route}${permissionRoutes[firstAllowedPermission.toLowerCase().trim()]}`);
      } else {
        navigate(`/${consts.route}/dashboard`);
      }
    }
    setLoading(false);
  };

  const onFinish = async (values) => {
    if (submitLockedRef.current) return;

    submitLockedRef.current = true;
    console.log("Login Success:", values.email);
    try {
      setLoading(true);
      const emailRegex =
        /[a-zA-Z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,8}(.[a-z{2,8}])?/g;
      const emailcheck = emailRegex.test(values.email);
      if (values.email == undefined) {
        await finishLoginAttempt("error", "Please Enter Email");
      } else if (!emailcheck) {
        await finishLoginAttempt("error", "Invalid Email");
      } else if (values.password == undefined) {
        await finishLoginAttempt("error", "Please Enter Password");
      } else if (values.password.length <= 5) {
        await finishLoginAttempt("error", "Password must be at least 5 characters long");
      } else {
        const email = values.email;
        const password = values.password;
        const payload = {
          email,
          password,
        };

        const { data } = await axios.post(
          `${consts.BackendUrl}/adminlogin`,
          payload,
        );
        if (data.success) {
          if (data?.result?.f2A_Status !== "true") {
            localStorage.setItem("Jetix", data?.result?.token);
            await delay(BUTTON_LOADER_DELAY);
            setLoading(false);
            showLoginMessage("success", data?.message);

            // Fetch profile and permissions
            dispatch(fetchAdmin())
              .then((res) => {
                if (res.payload?.success) {
                  const userRole = res.payload.result.role;
                  const userPermissions = res.payload.result.access?.permissions || [];
                  setTimeout(() => responseTimer(userRole, userPermissions), 1000);
                } else {
                  navigate(`/${consts.route}/dashboard`);
                }
              })
              .catch(() => {
                navigate(`/${consts.route}/dashboard`);
              })
              .finally(() => {
                submitLockedRef.current = false;
              });
          } else {
            await delay(BUTTON_LOADER_DELAY);
            setLoading(false);
            submitLockedRef.current = false;
            setAdminEmail(data?.result?.email);
            setOtpModel(true);
          }
        } else {
          await finishLoginAttempt("error", data?.message);
        }
      }
    } catch (error) {
      console.log(error.response?.data?.message);
      await finishLoginAttempt("error", error.response?.data?.message);
    }
  };

  // const onVerifyOtp = async () => {
  //   try {
  //     if (!fullOtp) {
  //       messageApi.open({
  //         type: 'error',
  //         content: 'Please Enter OTP',
  //       });
  //       return
  //     }
  //     if (fullOtp.length !== 6) {
  //       messageApi.open({
  //         type: 'error',
  //         content: 'Invalid OTP',
  //       });
  //       return
  //     }
  //     const payload = {
  //       email: adminemail,
  //       secret: fullOtp,
  //       status: "login"
  //     };
  //     const { data } = await axios.post(
  //       `${consts.BackendUrl}/verifyadmin2fa`,
  //       payload
  //     );
  //     if (data.success) {
  //       localStorage.setItem("Jetix", data?.result?.token);

  //       // Fetch profile and permissions
  //       const getProfile = async () => {
  //         try {
  //           const response = await axios.post(`${consts.BackendUrl}/getAdmin`, {}, {
  //             headers: {
  //               Authorization: `Bearer ${data?.result?.token}`,
  //             },
  //           });
  //           if (response?.data?.success) {
  //             const permissions = response.data.result.access?.permissions || [];
  //             const role = response.data.result.role;
  //             localStorage.setItem("permissions", JSON.stringify(permissions));
  //             localStorage.setItem("role", role);
  //           }
  //         } catch (error) {
  //           console.log("Failed to fetch admin profile", error);
  //         }
  //       };
  //       await getProfile();

  //       messageApi.open({
  //         type: 'success',
  //         content: data?.message,
  //       });
  //       setTimeout(() => {
  //         const permissions = JSON.parse(localStorage.getItem("permissions") || "[]");
  //         const role = localStorage.getItem("role");
  //         const normalizedPermissions = permissions.map(p => p.toLowerCase().trim());

  //         if (role === "admin" || role === "superadmin" || normalizedPermissions.includes("dashboard")) {
  //           navigate(`/${consts.route + '/dashboard'}`);
  //         } else {
  //           // Find first available permission route
  //           const permissionRoutes = {
  //             "users": "/usersHistory",
  //             "users wallet history": "/users",
  //             "crypto history": "/crypto/highlow",
  //             "trade settings": "/tradesetting",
  //             "deposit": "/deposit",
  //             "withdraw": "/withdraw",
  //             "pairs": "/pairs",
  //             "supports": "/supports",
  //             "settings": "/settings",
  //             "sub admin": "/subadmin/rolemanage"
  //           };

  //           // Find first allowed route by checking normalized keys
  //           const firstAllowedPermission = permissions.find(p => permissionRoutes[p.toLowerCase().trim()]);

  //           if (firstAllowedPermission) {
  //             navigate(`/${consts.route}${permissionRoutes[firstAllowedPermission.toLowerCase().trim()]}`);
  //           } else {
  //             navigate(`/${consts.route}/dashboard`);
  //           }
  //         }
  //       }, 1000);
  //     } else {
  //       messageApi.open({
  //         type: 'error',
  //       }
  //       );
  //     }
  //   } catch (error) {
  //     console.log(error.response?.data?.message);
  //     messageApi.open({
  //       type: 'error',
  //       content: error.response?.data?.message,
  //     });
  //   }
  // }

  useEffect(() => {
    if (fullOtp.length === 6) {
      onVerifyOtp(); //  auto login
    }
  }, [fullOtp]);

  const onVerifyOtp = async () => {
    try {
      if (!fullOtp || fullOtp.length !== 6) return;

      const payload = {
        email: adminemail,
        secret: fullOtp,
        status: "login",
      };

      const { data } = await axios.post(
        `${consts.BackendUrl}/verifyadmin2fa`,
        payload,
      );

      if (data.success) {
        showLoginMessage("success", data?.message);
        localStorage.setItem("Jetix", data?.result?.token);

        const response = await dispatch(fetchAdmin());
        console.log("🚀 ~ onVerifyOtp ~ response:", response)

        if (response?.payload?.success) {
          const userRole = response.payload.result.role;
          const userPermissions = response.payload.result.access?.permissions || [];
          setTimeout(() => responseTimer(userRole, userPermissions), 1000);
        } else {
          navigate(`/${consts.route}/dashboard`);
        }
      }
    } catch (error) {
      showLoginMessage("error", error.response?.data?.message);
      setOtp(["", "", "", "", "", ""]);
    }
    clearOtp();
  };

  return (
    <>
      <div className="login-container">
        <Card className="login-card">
          <div className="logo text-center">
            <img src={logo} width={103} height={"100%"} />
          </div>
          <h4 className="login-title ">Login</h4>
          <Form
            name="login"
            layout="vertical"
            onFinish={onFinish}
            initialValues={{ remember: true }}
          >
            {/* Email */}
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: "Please enter your email!" },
                { type: "email", message: "Enter a valid email!" },
              ]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="Enter your email"
                size="large"
              />
            </Form.Item>

            {/* Password */}
            <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: true, message: "Please enter your password!" },
              ]}
            >
              <Input.Password
                size="large"
                prefix={<LockOutlined />}
                placeholder="Enter your password"
              />
            </Form.Item>

            {/* Submit Button */}
            <Form.Item className="login-btn">
              <Button
                type="primary"
                htmlType="submit"
                block
                loading={loading}
                disabled={loading || submitLockedRef.current}
                iconPosition="end"
              >
                Login
              </Button>
            </Form.Item>
          </Form>
        </Card>
        <Modal
          open={otpModel}
          centered
          footer={null}
          onCancel={() => {
            setOtpModel(false);
            clearOtp();
          }}
          className="twofa-madel-box"
          onOk={() => onVerifyOtp()}
          okButtonProps={{ className: "ant-btn-variant-solid-2fa" }}
          // onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          maskClosable={false}
          maskStyle={{
            backdropFilter: "blur(8px)",
            backgroundColor: "rgba(0, 0, 0, 0.2)",
          }}
          styles={{
            content: {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              color: "white",

              // transform: "translate(50px, 50px)",
            },
            header: {
              backgroundColor: "transparent",
            },
          }}
        >
          <div>
            <form className="twofa-form-box">
              {/* <span className="mainHeading">Enter OTP</span> */}
              <p className="otpSubheading">Please Enter 2FA Auth code</p>
              <div className="inputContainer">
                {[0, 1, 2, 3, 4, 5].map((_, index) => (
                  <input
                    key={index}
                    type="password"
                    maxLength={1}
                    className="otp-input"
                    value={otp[index]}
                    required
                    onPaste={index === 0 ? handlePaste : undefined}
                    ref={(el) => (inputsRef.current[index] = el)}
                    onChange={(e) => {
                      handleOtp(e, index);
                    }}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                  />
                ))}
              </div>
              {/* <button className="verifyButton" type="submit">
              Verify
            </button>
            <button
              className="exitBtn"
              onClick={() => {
                setOtpModel(false);
              }}
            >
              ×
            </button> */}
            </form>
          </div>
        </Modal>
        {contextHolder}
      </div>
    </>
  );
};

export default Login;
